// -----------------------------------------------------------------------------
// TreatmentContextHeader
//
// Pure display header for the COMPLETED-TREATMENT wrap-up hub. Reads everything
// from the `row` passed by the lead table — NO API calls, NO Redux.
//
// Row shape note (investigated): the TREATMENT_STARTED grid rows are produced by
// parseV2ToV1 (src/utils/fieldUtils.js), which spreads the lead fields to the
// top level and nests the treatment under `row.lead_treatments[0]` (with
// `treatment_data` = the v2 treatment object, `treatment` = its name). The raw
// V2-flat shape (row.lead.first_name, row.treatment, row.price …) only exists
// pre-parse. To be backward-compatible with BOTH shapes, every field is read
// from the nested treatment first, then falls back to the flat field.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import moment from 'moment';
import { Box, Typography, Stack } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { getTransitionDate } from 'src/utils/dataGridUtils';
import { piiProps } from 'src/components/DemoBlur';

// Read a treatment-scoped field from either the parsed (nested) or raw (flat) row.
function pickTreatmentField(row, key) {
    const tx = row?.lead_treatments?.[0];
    if (tx && tx[key] != null) return tx[key];
    return row?.[key];
}

// Resolve the treatment NAME robustly across the shapes we actually see.
// On the V2-flat row `treatment` is an OBJECT (treatment.treatment_item.name),
// while the parsed/nested shape exposes treatment_data.treatment_item.name and
// some legacy rows carry treatment as a plain string. Take the first non-empty.
function resolveTreatmentName(row) {
    const tx = row?.lead_treatments?.[0];
    // `useLeadTableHelper` row shape (the actual one reaching this hub): the name
    // lives in `treatment_name`, and per-treatment data under `treatmentData[0]`.
    const txd = row?.treatmentData?.[0];
    const candidates = [
        // useLeadTableHelper row (confirmed runtime shape)
        typeof row?.treatment_name === 'string' ? row.treatment_name : '',
        txd?.treatment_data?.treatment_item?.name,
        typeof txd?.treatment === 'string' ? txd.treatment : '',
        // nested parsed shape
        tx?.treatment_data?.treatment_item?.name,
        // flat object shape
        row?.treatment?.treatment_item?.name,
        // flat string shape (fallback)
        typeof row?.treatment === 'string' ? row.treatment : '',
        // legacy/edge fallbacks
        tx?.treatment_data?.name,
        typeof tx?.treatment === 'string' ? tx.treatment : '',
    ];
    const name = candidates.find((c) => typeof c === 'string' && c.trim() !== '');
    return name || '';
}

function formatStartedDate(stateTransitionLogs) {
    const started = getTransitionDate(stateTransitionLogs);
    if (!started) return null;
    // getTransitionDate returns either a moment (multi-log case) or a
    // 'DD-MM-YYYY HH:mm:ss' string (single-log case).
    const m = moment.isMoment(started) ? started : moment(started, 'DD-MM-YYYY HH:mm:ss');
    if (!m.isValid()) return null;
    return m;
}

export default function TreatmentContextHeader({ row }) {
    // Patient name — parsed rows spread lead fields to top level; raw rows nest
    // under row.lead. Support both.
    const firstName = row?.lead?.first_name ?? row?.first_name ?? '';
    const lastName = row?.lead?.last_name ?? row?.last_name ?? '';
    const patientName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Patient';

    const treatmentName = resolveTreatmentName(row) || '—';
    const price = pickTreatmentField(row, 'price');
    const isFeePending = pickTreatmentField(row, 'is_treatment_fee_pending');
    const payments = pickTreatmentField(row, 'lead_treatment_payments') || [];
    const paymentMethod = Array.isArray(payments) ? payments?.[0]?.payment_type : null;
    const hasClinCheck = !!pickTreatmentField(row, 'leadtreatment_clincheck');
    const appointments = pickTreatmentField(row, 'appointments') || [];
    const appointmentCount = Array.isArray(appointments) ? appointments.length : 0;

    const startedMoment = formatStartedDate(pickTreatmentField(row, 'state_transition_logs'));
    const startedLabel = startedMoment ? startedMoment.format('D MMM YYYY') : null;
    const durationLabel = startedMoment ? startedMoment.fromNow(true) : null;

    const priceLabel = price != null && price !== '' ? `£${price}` : '—';

    const rows = [
        { icon: 'bxs:pound', label: 'Price', value: priceLabel },
        {
            icon: isFeePending ? 'eva:alert-circle-fill' : 'eva:checkmark-circle-2-fill',
            label: 'Treatment fee',
            value: isFeePending ? 'Pending' : 'Paid',
            color: isFeePending ? '#B76E00' : '#118D57',
        },
        { icon: 'eva:credit-card-fill', label: 'Payment method', value: paymentMethod || '—' },
        { icon: 'eva:calendar-fill', label: 'Appointments', value: appointmentCount },
        {
            icon: hasClinCheck ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-outline',
            label: 'ClinCheck',
            value: hasClinCheck ? 'Present' : 'None',
            color: hasClinCheck ? '#118D57' : undefined,
        },
        ...(startedLabel ? [{ icon: 'eva:clock-fill', label: 'Started', value: startedLabel }] : []),
    ];

    return (
        <Box data-testid="hub-treatment-context" sx={{ width: '100%' }}>
            {/* Name + status row */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#212B36', lineHeight: 1.3 }}>
                    <span {...piiProps}>{patientName}</span>
                </Typography>
                <Box
                    component="span"
                    sx={{
                        px: 0.875, py: 0.125, borderRadius: '980px', bgcolor: 'rgba(255,171,0,0.14)', color: '#B07A00',
                        fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.01em', textTransform: 'uppercase', lineHeight: 1.5,
                    }}
                >
                    In progress
                </Box>
            </Stack>
            <Typography sx={{ fontSize: '12.5px', color: '#637381', mt: 0.25 }}>
                {treatmentName}
                {startedLabel && durationLabel ? ` · ${durationLabel} in` : ''}
            </Typography>

            {/* Tidy key-value grid */}
            <Box
                sx={{
                    mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 2, rowGap: 0.25,
                    '& > div': { borderBottom: '1px solid rgba(145,158,171,0.12)' },
                }}
            >
                {rows.map((r) => (
                    <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, minWidth: 0 }}>
                        <Iconify icon={r.icon} width={14} height={14} sx={{ color: r.color || '#919EAB', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '11.5px', color: '#637381', flex: 1, whiteSpace: 'nowrap' }}>{r.label}</Typography>
                        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: r.color || '#212B36', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {r.value}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

TreatmentContextHeader.propTypes = {
    row: PropTypes.object.isRequired,
};
